import React, { useEffect, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import AuthPreloader from './AuthPreloader'
import { useAuth0Redux } from '@/hooks/useAuth0Redux'

// Routes that allow unauthenticated (view-only) access.
// Pattern uses startsWith matching against location.pathname.
const PUBLIC_ACCESS_ROUTES = [
  '/devices/',  // matches /devices/:deviceId
]

interface AuthGuardProps {
  children: React.ReactNode
  requiredRoles?: string[]
}

/**
 * AuthGuard
 * - Ensures Auth0 state is initialized and synced to Redux
 * - Triggers loginWithRedirect if unauthenticated (preserves return path)
 * - Optionally enforces required roles
 * - Allows view-only access for PUBLIC_ACCESS_ROUTES when unauthenticated
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children, requiredRoles }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const location = useLocation()

  // Ensure Redux sync runs
  useAuth0Redux()

  // Check if current route allows unauthenticated access
  const isPublicRoute = useMemo(() => {
    return PUBLIC_ACCESS_ROUTES.some(route => location.pathname.startsWith(route))
  }, [location.pathname])

  // roles from Redux (populated by useAuth0Redux token parsing)

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublicRoute) {
      const params = new URLSearchParams(location.search);
      const screen_hint = params.get("screen_hint");
      const login_hint = params.get("login_hint");
      // Start interactive login and preserve the current path
      if(screen_hint || login_hint) {
        loginWithRedirect({
          appState: { returnTo: location.pathname + location.search },
          authorizationParams: {
             ...(screen_hint && { screen_hint }),
            ...(login_hint && { login_hint }),
          }
        }).catch((e) => {
          console.error('loginWithRedirect failed', e)
        })
      }
      else {
      loginWithRedirect({ appState: { returnTo: location.pathname + location.search } }).catch((e) => {
        console.error('loginWithRedirect failed', e)
      })
      }
      
    }
  }, [isLoading, isAuthenticated, isPublicRoute, loginWithRedirect, location])

  // For public routes, allow rendering even when not authenticated (skip preloader)
  if (isPublicRoute && !isLoading) {
    return <>{children}</>
  }

  if (isLoading || !isAuthenticated) {
    return <AuthPreloader />
  }

  return <>{children}</>
}

export default AuthGuard
